import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Row, Col, Badge, Card } from 'react-bootstrap';
import { getVehicles, createVehicle, updateVehicle, deleteVehicle, getVehicleTypes, getDrivers, getBases, getSettlementGroups, getProviders } from '../api';
import SearchableSelect from './SearchableSelect';
import MultiSearchSelect from './MultiSearchSelect';

const VehiclesView = () => {
    const [vehicles, setVehicles] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [drivers, setDrivers] = useState([]); 
    const [bases, setBases] = useState([]);
    const [settlementGroups, setSettlementGroups] = useState([]);
    const [providers, setProviders] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [driverSearchTerm, setDriverSearchTerm] = useState('');
    const [mobileSearchTerm, setMobileSearchTerm] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('');
    const [page, setPage] = useState(0);
    const [limit] = useState(10);
    const [hasMore, setHasMore] = useState(true);
    const [showAll, setShowAll] = useState(false);

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // Form State
    const initialFormState = {
        internal_code: '', 
        plate: '', 
        model: '', 
        make: '', 
        year: 2024,
        color: '', 
        vehicle_type_id: '',
        owner_id: '',
        current_driver_id: '',
        permitted_base_ids: [],
        permitted_driver_ids: [],
        settlement_group_id: '',
        provider_id: '',
        is_active: true
    };
    const [formData, setFormData] = useState(initialFormState);

    const loadData = useCallback(async () => {
        try {
            const params = {
                skip: page * limit,
                limit: limit,
                q: mobileSearchTerm || searchTerm,
                driver_q: driverSearchTerm,
                settlement_group_id: selectedGroup || undefined
            };
            if (selectedProvider) {
                params.provider_id = parseInt(selectedProvider);
            }
            const [vData, vtData, dData, bData, sgData, pData] = await Promise.all([
                (searchTerm || driverSearchTerm || mobileSearchTerm || showAll || selectedGroup || selectedProvider) ? getVehicles(params) : Promise.resolve([]), 
                getVehicleTypes(), 
                getDrivers({ limit: 1000 }),
                getBases(), 
                getSettlementGroups(),
                getProviders()
            ]);
            
            let finalVehicles = vData;
            if (mobileSearchTerm) {
                finalVehicles = vData.filter(v => 
                    v.internal_code === mobileSearchTerm || 
                    (v.internal_code && v.internal_code.replace('M-', '') === mobileSearchTerm)
                );
            }
            
            setVehicles(finalVehicles);
            setHasMore(vData.length === limit);
            setVehicleTypes(vtData);
            setDrivers(dData);
            setBases(bData);
            setSettlementGroups(sgData);
            setProviders(pData || []);
        } catch (error) {
            console.error("Error loading vehicles data", error);
        }
    }, [page, limit, searchTerm, driverSearchTerm, mobileSearchTerm, showAll, selectedGroup, selectedProvider]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            loadData();
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [loadData]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPage(0);
    };

    const handleDriverSearchChange = (e) => {
        setDriverSearchTerm(e.target.value);
        setPage(0);
    };

    const handleOpenCreate = () => {
        setFormData(initialFormState);
        setIsEditing(false);
        setEditingId(null);
        setShowModal(true);
    };

    const handleOpenEdit = (vehicle) => {
        setFormData({
            internal_code: vehicle.internal_code || '',
            plate: vehicle.plate,
            model: vehicle.model || '',
            make: vehicle.make || '',
            year: vehicle.year || 2024,
            color: vehicle.color || '',
            vehicle_type_id: vehicle.vehicle_type_id || '',
            owner_id: vehicle.owner_id || '',
            current_driver_id: vehicle.current_driver_id || '',
            permitted_base_ids: vehicle.permitted_bases?.map(b => b.id) || [],
            permitted_driver_ids: vehicle.permitted_drivers?.map(d => d.id) || [],
            settlement_group_id: vehicle.settlement_group_id || '',
            provider_id: vehicle.provider_id || '',
            is_active: vehicle.is_active
        });
        setIsEditing(true);
        setEditingId(vehicle.id);
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                vehicle_type_id: parseInt(formData.vehicle_type_id),
                owner_id: parseInt(formData.owner_id),
                current_driver_id: formData.current_driver_id ? parseInt(formData.current_driver_id) : null,
                settlement_group_id: formData.settlement_group_id ? parseInt(formData.settlement_group_id) : null,
                provider_id: formData.provider_id ? parseInt(formData.provider_id) : null,
                year: parseInt(formData.year),
                permitted_driver_ids: formData.permitted_driver_ids.map(Number)
            };

            if (isEditing) {
                await updateVehicle(editingId, payload);
            } else {
                await createVehicle(payload);
            }
            setShowModal(false);
            loadData();
        } catch (error) {
            alert("Error al guardar vehículo. Verifique Patente o Código Interno únicos.");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Estás seguro de eliminar este vehículo?")) {
            try {
                await deleteVehicle(id);
                loadData();
            } catch (e) { alert("Error eliminando vehículo"); }
        }
    };

    const toggleBase = (baseId) => {
        setFormData(prev => {
            const current = prev.permitted_base_ids;
            if (current.includes(baseId)) {
                return { ...prev, permitted_base_ids: current.filter(id => id !== baseId) };
            } else {
                return { ...prev, permitted_base_ids: [...current, baseId] };
            }
        });
    };

    const owners = drivers.filter(d => d.is_owner);
    const chauffeurs = drivers.filter(d => d.is_driver);

    return (
        <div className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>🚙 Gestión de Flota (Móviles)</h3>
                <div className="d-flex gap-2">
                    <Form.Control 
                        placeholder="🔢 Nº Móvil..." 
                        value={mobileSearchTerm} 
                        onChange={(e) => { setMobileSearchTerm(e.target.value); setPage(0); }} 
                        style={{maxWidth: '120px'}}
                    />
                    <Form.Control 
                        placeholder="🔍 Patente / Código..." 
                        value={searchTerm} 
                        onChange={handleSearchChange} 
                        style={{maxWidth: '200px'}}
                    />
                    <Form.Control 
                        placeholder="👤 Nombre Conductor..." 
                        value={driverSearchTerm} 
                        onChange={handleDriverSearchChange} 
                        style={{maxWidth: '200px'}}
                    />
                    <Form.Select 
                        value={selectedGroup} 
                        onChange={(e) => { setSelectedGroup(e.target.value); setPage(0); }} 
                        style={{maxWidth: '190px'}}
                    >
                        <option value="">🔍 Todos los Grupos</option>
                        <option value="none">⚠️ Sin Grupo de Pago</option>
                        {settlementGroups.map(g => (
                            <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                    </Form.Select>
                    <Form.Select 
                        value={selectedProvider} 
                        onChange={(e) => { setSelectedProvider(e.target.value); setPage(0); }} 
                        style={{maxWidth: '200px'}}
                    >
                        <option value="">🏢 Todos los Proveedores</option>
                        {providers.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </Form.Select>
                    <Button variant="outline-primary" onClick={() => setShowAll(true)}>👁️ Ver Todos</Button>
                    <Button variant="success" onClick={handleOpenCreate}>+ Nuevo Móvil</Button>
                </div>
            </div>

            <Card className="shadow-sm border-0">
                <Table hover responsive className="align-middle mb-0">
                    <thead className="bg-light">
                        <tr>
                            <th>Código / Patente</th>
                            <th>Vehículo</th>
                            <th>Tipo</th>
                            <th>Dueño (Partner)</th>
                            <th>Conductor Actual</th>
                            <th>Grupo de Pago</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {vehicles.map(v => (
                            <tr key={v.id}>
                                <td>
                                    <Badge bg="dark" className="me-2" style={{fontSize: '0.9em'}}>{v.internal_code}</Badge>
                                    <span className="fw-bold text-muted">{v.plate}</span>
                                </td>
                                <td>
                                    <div>{v.make} {v.model}</div>
                                    <small className="text-muted">{v.color} - {v.year}</small>
                                </td>
                                <td>
                                    {vehicleTypes.find(t => t.id === v.vehicle_type_id)?.name || 'N/A'}
                                </td>
                                <td>
                                    <div>{drivers.find(d => d.id === v.owner_id)?.full_name || <span className="text-danger small">ID: {v.owner_id}</span>}</div>
                                    <div>
                                        {v.provider_id ? (
                                            <Badge bg="purple" style={{ backgroundColor: '#a855f7' }}>
                                                {providers.find(p => p.id === v.provider_id)?.name || 'Prov: ' + v.provider_id}
                                            </Badge>
                                        ) : <small className="text-muted">Propio</small>}
                                    </div>
                                </td>
                                <td>
                                    {v.current_driver_id ? (
                                        <Badge bg="info" className="text-dark">
                                            {drivers.find(d => d.id === v.current_driver_id)?.full_name || 'ID: ' + v.current_driver_id}
                                        </Badge>
                                    ) : <span className="text-muted small">- Disponible -</span>}
                                </td>
                                <td>
                                    {v.settlement_group_id ? (
                                        <Badge bg="primary" style={{ backgroundColor: '#2563eb' }}>
                                            {settlementGroups.find(g => g.id === v.settlement_group_id)?.name || 'Grupo ' + v.settlement_group_id}
                                        </Badge>
                                    ) : (
                                        <Badge bg="warning" text="dark" className="bg-opacity-25" style={{ backgroundColor: '#fef3c7', color: '#d97706' }}>
                                            Sin Grupo
                                        </Badge>
                                    )}
                                </td>
                                <td>
                                    <div className="d-flex flex-column gap-1">
                                        {v.is_active ? <Badge bg="success">Activo</Badge> : <Badge bg="secondary">Inactivo</Badge>}
                                        {v.permitted_bases?.map(b => (
                                            <Badge key={b.id} bg="light" text="dark" className="border" style={{fontSize: '0.65rem'}}>{b.name}</Badge>
                                        ))}
                                    </div>
                                </td>
                                <td>
                                    <Button size="sm" variant="outline-primary" className="me-1" onClick={() => handleOpenEdit(v)}>✏️</Button>
                                    <Button size="sm" variant="outline-danger" onClick={() => handleDelete(v.id)}>🗑️</Button>
                                </td>
                            </tr>
                        ))}
                        {vehicles.length === 0 && (
                            <tr>
                                <td colSpan="8" className="text-center p-4 text-muted">
                                    {(!searchTerm && !driverSearchTerm && !selectedGroup && !mobileSearchTerm) ? '🔍 Ingrese un término de búsqueda o seleccione un filtro para ver los vehículos.' : 'No se encontraron vehículos.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>

                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                    <div className="small text-muted">Página {page + 1}</div>
                    <div className="d-flex gap-2">
                        <Button variant="outline-secondary" size="sm" disabled={page === 0} onClick={() => setPage(prev => prev - 1)}>Anterior</Button>
                        <Button variant="outline-secondary" size="sm" disabled={!hasMore} onClick={() => setPage(prev => prev + 1)}>Siguiente</Button>
                    </div>
                </div>
            </Card>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton><Modal.Title>{isEditing ? 'Editar Móvil' : 'Nuevo Móvil'}</Modal.Title></Modal.Header>
                    <Modal.Body>
                        <Row className="mb-3">
                            <Col md={12}><h6 className="text-muted border-bottom pb-1">Identificación</h6></Col>
                            <Col md={6}>
                                <Form.Label>Código Interno (Radio)</Form.Label>
                                <Form.Control required placeholder="Ej: M-105" value={formData.internal_code} onChange={e => setFormData({...formData, internal_code: e.target.value})} />
                            </Col>
                            <Col md={6}>
                                <Form.Label>Patente</Form.Label>
                                <Form.Control required placeholder="Ej: ABCD-12" value={formData.plate} onChange={e => setFormData({...formData, plate: e.target.value.toUpperCase()})} />
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={12}><h6 className="text-muted border-bottom pb-1">Características</h6></Col>
                            <Col md={4}><Form.Label>Marca</Form.Label><Form.Control value={formData.make} onChange={e => setFormData({...formData, make: e.target.value})} /></Col>
                            <Col md={4}><Form.Label>Modelo</Form.Label><Form.Control value={formData.model} onChange={e => setFormData({...formData, model: e.target.value})} /></Col>
                            <Col md={4}><Form.Label>Año</Form.Label><Form.Control type="number" value={formData.year} onChange={e => setFormData({...formData, year: e.target.value})} /></Col>
                            <Col md={4} className="mt-2"><Form.Label>Color</Form.Label><Form.Control value={formData.color} onChange={e => setFormData({...formData, color: e.target.value})} /></Col>
                            <Col md={8} className="mt-2">
                                <Form.Label>Tipo de Vehículo</Form.Label>
                                <Form.Select required value={formData.vehicle_type_id} onChange={e => setFormData({...formData, vehicle_type_id: e.target.value})}>
                                    <option value="">- Seleccione -</option>
                                    {vehicleTypes.map(v => <option key={v.id} value={v.id}>{v.name} (Cap: {v.capacity})</option>)}
                                </Form.Select>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={12}><h6 className="text-muted border-bottom pb-1">Asignación y Pool de Conductores</h6></Col>
                            <Col md={6}>
                                <Form.Label className="text-primary fw-bold">Dueño (Partner)</Form.Label>
                                <SearchableSelect 
                                    options={owners} 
                                    value={formData.owner_id} 
                                    onChange={val => setFormData({...formData, owner_id: val})} 
                                    placeholder="Seleccione Dueño..."
                                  />
                            </Col>
                            <Col md={6}>
                                <Form.Label className="text-success fw-bold">Conductor Actual (En Turno)</Form.Label>
                                <SearchableSelect 
                                    options={chauffeurs} 
                                    value={formData.current_driver_id} 
                                    onChange={val => setFormData({...formData, current_driver_id: val})} 
                                    placeholder="Sin Asignar (Disponible)..."
                                  />
                                <div className="mt-1 text-end">
                                    <Button 
                                        variant="link" 
                                        size="sm" 
                                        className="text-danger p-0 text-decoration-none"
                                        onClick={() => setFormData({...formData, current_driver_id: ''})}
                                        style={{ fontSize: '0.75rem' }}
                                    >
                                        ✕ Liberar Vehículo (Dejar Disponible)
                                    </Button>
                                </div>
                            </Col>
                            <Col md={12} className="mt-3">
                                <Form.Label className="fw-bold text-danger">Pool de Conductores Autorizados</Form.Label>
                                <MultiSearchSelect 
                                    options={chauffeurs}
                                    value={formData.permitted_driver_ids}
                                    onChange={(val) => setFormData({ ...formData, permitted_driver_ids: val })}
                                    placeholder="Buscar por Nombre o RUT..."
                                />
                                <Form.Text className="text-muted italic">Móviles que el conductor podrá elegir al iniciar sesión.</Form.Text>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={12}><h6 className="text-muted border-bottom pb-1">Configuración Operativa</h6></Col>
                            <Col md={6}>
                                <Form.Label className="fw-bold">Grupo de Liquidación</Form.Label>
                                <Form.Select value={formData.settlement_group_id} onChange={e => setFormData({...formData, settlement_group_id: e.target.value})}>
                                    <option value="">- Por Defecto -</option>
                                    {settlementGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                </Form.Select>
                            </Col>
                            <Col md={6}>
                                <Form.Label className="fw-bold">Proveedor Asociado</Form.Label>
                                <Form.Select value={formData.provider_id} onChange={e => setFormData({...formData, provider_id: e.target.value})}>
                                    <option value="">- Ninguno (Flota Propia) -</option>
                                    {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </Form.Select>
                            </Col>
                            <Col md={12} className="mt-2">
                                <Form.Label>Bases Permitidas</Form.Label>
                                <div className="d-flex flex-wrap gap-2">
                                    {bases.map(b => (
                                        <Button 
                                            key={b.id}
                                            size="sm" 
                                            variant={formData.permitted_base_ids.includes(b.id) ? "primary" : "outline-secondary"}
                                            onClick={() => toggleBase(b.id)}
                                        >
                                            {b.name}
                                        </Button>
                                    ))}
                                </div>
                            </Col>
                        </Row>

                        <Form.Group className="mt-3">
                            <Form.Check type="switch" label="Vehículo Activo" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
                        <Button variant="primary" type="submit">{isEditing ? 'Guardar Cambios' : 'Registrar Móvil'}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default VehiclesView;
